/**
 * Minimal dependency-free markdown renderer for assistant chat output.
 *
 * The source is HTML-escaped before any transformation, so the returned
 * markup can only contain tags generated here — never raw HTML from the
 * model. Supported subset: fenced code blocks, inline code, headings,
 * ordered/unordered lists (one nesting level), blockquotes, pipe tables,
 * bold, italic, strikethrough, links and horizontal rules.
 */

const STANDIN = "\u0000";

interface InlineToken {
    placeholder: string;
    html: string;
}

interface ListBlock {
    ordered: boolean;
    items: string[];
    sub: { ordered: boolean; items: string[] } | null;
}

const escapeHtml = (text: string): string =>
    text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

const isSafeLink = (url: string): boolean =>
    /^(https?:\/\/|mailto:)/i.test(url);

const renderInline = (text: string): string => {
    const tokens: InlineToken[] = [];
    let out = escapeHtml(text);

    // Inline code first so its content is untouched by the rules below.
    out = out.replace(/`([^`\n]+)`/g, (_match, code: string) => {
        const placeholder = `${STANDIN}i${tokens.length}${STANDIN}`;
        tokens.push({ placeholder, html: `<code>${code}</code>` });
        return placeholder;
    });

    out = out
        .replace(/\*\*\*([^*\n]+)\*\*\*/g, "<strong><em>$1</em></strong>")
        .replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>")
        .replace(/__([^_\n]+)__/g, "<strong>$1</strong>")
        .replace(/(^|[\s(])\*([^*\n]+)\*(?=$|[\s.,;:!?)])/g, "$1<em>$2</em>")
        .replace(/~~([^~\n]+)~~/g, "<del>$1</del>")
        .replace(
            /\[([^\]]+)\]\(([^)\s]+)\)/g,
            (match: string, label: string, url: string) =>
                isSafeLink(url)
                    ? `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`
                    : match,
        );

    for (const token of tokens) {
        out = out.split(token.placeholder).join(token.html);
    }

    return out;
};

const renderTableCells = (row: string): string[] =>
    row
        .trim()
        .replace(/^\|/, "")
        .replace(/\|$/, "")
        .split("|")
        .map((cell) => cell.trim());

const isTableSeparator = (line: string): boolean =>
    line.includes("-") && /^\s*\|?\s*:?-{3,}[-\s|:]*\|?\s*$/.test(line);

const LIST_ITEM = /^(\s*)([-*+]|\d+[.)])\s+(.+)$/;
const RAW_BLOCK = new RegExp(`^${STANDIN}c\\d+${STANDIN}$`);

const renderBlocks = (source: string): string => {
    const lines = source.split("\n");
    const blocks: string[] = [];
    let paragraph: string[] = [];
    let quote: string[] = [];
    let list: ListBlock | null = null;
    let table: { header: string[]; rows: string[][] } | null = null;

    const flushParagraph = (): void => {
        if (paragraph.length === 0) return;

        blocks.push(`<p>${paragraph.map(renderInline).join("<br>")}</p>`);
        paragraph = [];
    };

    const flushQuote = (): void => {
        if (quote.length === 0) return;

        blocks.push(
            `<blockquote>${quote.map(renderInline).join("<br>")}</blockquote>`,
        );
        quote = [];
    };

    const flushTable = (): void => {
        if (!table) return;

        const head = table.header
            .map((cell) => `<th>${renderInline(cell)}</th>`)
            .join("");
        const body = table.rows
            .map(
                (row) =>
                    `<tr>${row
                        .map((cell) => `<td>${renderInline(cell)}</td>`)
                        .join("")}</tr>`,
            )
            .join("");
        blocks.push(
            `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`,
        );
        table = null;
    };

    const flushSubList = (): void => {
        if (!list?.sub) return;

        const tag = list.sub.ordered ? "ol" : "ul";
        const html = list.sub.items.map((item) => `<li>${item}</li>`).join("");
        list.items[list.items.length - 1] += `<${tag}>${html}</${tag}>`;
        list.sub = null;
    };

    const flushList = (): void => {
        if (!list) return;

        flushSubList();
        const tag = list.ordered ? "ol" : "ul";
        const html = list.items.map((item) => `<li>${item}</li>`).join("");
        blocks.push(`<${tag}>${html}</${tag}>`);
        list = null;
    };

    const flushAll = (): void => {
        flushParagraph();
        flushQuote();
        flushList();
        flushTable();
    };

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const heading = /^(#{1,6})\s+(.+)$/.exec(line);
        const listItem = LIST_ITEM.exec(line);

        if (RAW_BLOCK.test(line)) {
            flushAll();
            blocks.push(line);
            continue;
        }

        if (heading) {
            flushAll();
            const level = heading[1].length;
            blocks.push(`<h${level}>${renderInline(heading[2])}</h${level}>`);
            continue;
        }

        if (/^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
            flushAll();
            blocks.push("<hr>");
            continue;
        }

        if (/^>\s?/.test(line)) {
            flushParagraph();
            flushList();
            flushTable();
            quote.push(line.replace(/^>\s?/, ""));
            continue;
        }

        if (
            line.includes("|") &&
            i + 1 < lines.length &&
            isTableSeparator(lines[i + 1])
        ) {
            flushAll();
            table = { header: renderTableCells(line), rows: [] };
            i++;
            continue;
        }

        if (table && line.includes("|")) {
            table.rows.push(renderTableCells(line));
            continue;
        }

        if (list && line.trim() === "") {
            flushList();
            continue;
        }

        if (listItem) {
            flushParagraph();
            flushQuote();
            flushTable();
            const [, indent, marker, text] = listItem;
            const ordered = /\d/.test(marker);
            const nested = indent.length >= 2;

            if (!list) {
                list = { ordered, items: [], sub: null };
            } else if (nested) {
                if (!list.sub || list.sub.ordered !== ordered) {
                    flushSubList();
                    list.sub = { ordered, items: [] };
                }
                list.sub.items.push(renderInline(text));
                continue;
            } else {
                flushSubList();
                if (list.ordered !== ordered) {
                    flushList();
                    list = { ordered, items: [], sub: null };
                }
            }

            list.items.push(renderInline(text));
            continue;
        }

        flushQuote();
        flushList();
        flushTable();

        if (line.trim() === "") {
            flushParagraph();
        } else {
            paragraph.push(line);
        }
    }

    flushAll();

    return blocks.join("");
};

export const renderMarkdown = (source: string): string => {
    const normalized = (source ?? "").replace(/\r\n?/g, "\n").trim();

    if (!normalized) {
        return "";
    }

    const tokens: InlineToken[] = [];
    const store = (html: string): string => {
        const placeholder = `${STANDIN}c${tokens.length}${STANDIN}`;
        tokens.push({ placeholder, html });
        return placeholder;
    };

    // Fenced blocks are pulled out before escaping so their content keeps
    // its original characters; each is escaped individually when stored.
    const withoutFences = normalized
        .replace(/```([^`\n]+)```/g, (_match: string, code: string) =>
            store(`<code>${escapeHtml(code)}</code>`),
        )
        .replace(/```[^\n]*\n([\s\S]*?)```/g, (_match: string, code: string) =>
            store(
                `<pre><code>${escapeHtml(code.replace(/\n$/, ""))}</code></pre>`,
            ),
        )
        .replace(
            // A fence still open while the assistant is streaming.
            /```[^\n]*\n?([\s\S]*)$/,
            (match: string, code: string) =>
                code.trim()
                    ? store(`<pre><code>${escapeHtml(code)}</code></pre>`)
                    : match,
        );

    return renderBlocks(withoutFences).replace(
        new RegExp(`${STANDIN}c(\\d+)${STANDIN}`, "g"),
        (_match: string, index: string) => tokens[Number(index)].html,
    );
};
